import { queryOptions } from "@tanstack/react-query";
import { api } from "./api";
import { displayTeamInviteUsername } from "../utils/teamInviteDisplayUsername";
import type { Advertisement } from "../constants/advertisements";

/**
 * The queries behind the data hooks, without React, so code outside a
 * component (e.g. Ask Noticeboard's answers) can fetch the same data through
 * the same cache. The hooks add `enabled` and the signed-in user.
 */

/** Prefix for every dashboard-stats query — invalidate with this after notices/users change. */
export const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

/** The person a cached answer belongs to. Two people in one Organization do not share a fresh cache. */
export function viewerIdOf(
  user: { id?: string; email?: string; username?: string } | null | undefined
): string | undefined {
  const id = user?.id || user?.email || user?.username;
  return id || undefined;
}

/** Appends the viewer to a query key. Prefix invalidation (`["notices"]`) still matches. */
export function forViewer<T extends { queryKey: readonly unknown[] }>(options: T, viewerId: string | undefined): T {
  if (!viewerId) return options;
  return { ...options, queryKey: [...options.queryKey, viewerId] };
}

/** The query behind useNotices, shared with code outside a component (e.g. Ask Noticeboard). */
export const noticesQuery = (
  schoolId: string | undefined,
  token: string | null | undefined,
  options?: { excludeFeedHidden?: boolean }
) =>
  queryOptions({
    queryKey: ["notices", schoolId, options?.excludeFeedHidden ?? false],
    queryFn: async () => {
      if (!schoolId || !token) {
        throw new Error("Missing schoolId or token");
      }
      const fetchedNotices = await api.notices.getAll(schoolId, token, options);

      // Filter out deleted notices as a safety measure
      return fetchedNotices
        .filter((notice) => !notice.isDeleted)
        .map((notice) => ({
          ...notice,
          type: getValidNoticeType(notice.type),
        }));
    },
    staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
  });

/** The query behind useDashboardData, shared with code outside a component (e.g. Ask Noticeboard). */
export const dashboardStatsQuery = (
  schoolId: string | undefined,
  token: string | null | undefined,
  month?: string
) =>
  queryOptions({
    queryKey: [...DASHBOARD_QUERY_KEY, schoolId, month],
    queryFn: async () => {
      if (!schoolId || !token) {
        throw new Error("Missing schoolId or token");
      }
      return api.dashboard.getStats(schoolId, token, month);
    },
    staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
  });

/** The query behind useJoinRequests, shared with code outside a component (e.g. Ask Noticeboard). */
export const joinRequestsQuery = (
  orgId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["joinRequests", orgId],
    queryFn: async () => {
      if (!orgId || !token) {
        throw new Error("Missing organization ID or token");
      }
      return api.users.getJoinRequests(orgId, token);
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/** The query behind useUsers, shared with code outside a component (e.g. Ask Noticeboard). */
export const usersQuery = (
  orgId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["users", orgId],
    queryFn: async () => {
      if (!orgId || !token) {
        throw new Error("Missing organization ID or token");
      }
      return api.users.getAll(orgId, token);
    },
    staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
  });

/** The query behind useInvitations: the Users page's Pending Invitations. */
export const invitationsQuery = (
  orgId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["invitations", orgId],
    queryFn: async () => {
      if (!orgId || !token) {
        throw new Error("Missing organization ID or token");
      }
      return api.users.getInvitations(orgId, token);
    },
    staleTime: 30 * 1000, // Data is considered fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Cache is kept for 5 minutes
  });

/** The query behind useFamilySearchIndex: every child in a family, with their guardians. */
export const familySearchIndexQuery = (
  orgId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["family-search-index", orgId],
    queryFn: async () => {
      if (!orgId || !token) return [];
      return api.family.getSearchIndex(orgId, token);
    },
    staleTime: 60_000,
  });

/** The query behind useCircles, shared with code outside a component (e.g. Ask Noticeboard). */
export const circlesQuery = (
  schoolId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["circles", schoolId],
    queryFn: async () => {
      if (!schoolId || !token) {
        throw new Error("Missing schoolId or token");
      }
      const response = await api.circles.getAll(schoolId, token);
      return response.circles;
    },
    staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
  });

/** The query behind useTeamMembers: the Team, with usernames as Team shows them. */
export const teamMembersQuery = (
  schoolId: string | undefined,
  soid: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["teamMembers", schoolId, soid],
    queryFn: async () => {
      if (!schoolId || !token) {
        throw new Error("User or token not available");
      }
      const members = await api.team.getTeamMembers(schoolId, token);
      const sessionSoid = typeof soid === "string" ? soid.trim() || undefined : undefined;
      return members.map((m) => ({
        ...m,
        username: displayTeamInviteUsername(m.username, sessionSoid),
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

/** The query behind useCircleUsers: a Circle's members, as its page counts and lists them. */
export const circleUsersQuery = (
  subcircleId: string,
  schoolId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: ["circleUsers", subcircleId, schoolId],
    queryFn: async () => {
      if (!schoolId || !token) {
        throw new Error("Missing schoolId or token");
      }
      const response = await api.circles.getSubCircleUsers(subcircleId, schoolId, token);
      return response.users;
    },
  });

/**
 * The query behind useAdvertisements: all of the Organization's ads, drafts and expired included.
 * The "active" suffix keeps it apart from AdCenter's ["advertisements", schoolId], which also fetches cancelled ads.
 */
export const advertisementsQuery = (schoolId: string | undefined, token: string | null | undefined) =>
  queryOptions({
    queryKey: ["advertisements", schoolId, "active"],
    queryFn: async () => {
      if (!token) throw new Error("No token");
      const response = await api.advertisements.getAll(token);
      return response.advertisements.map((ad) => ({ ...ad, id: ad.advertisementId })) as Advertisement[];
    },
  });

/** Prefix of every Templates query; invalidate it after creating, editing or removing a Template. */
export const TEMPLATES_QUERY_KEY = ["templates"] as const;

/** The query behind useTemplates, shared with code outside a component (e.g. Ask Noticeboard). */
export const templatesQuery = (
  schoolId: string | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    queryKey: [...TEMPLATES_QUERY_KEY, schoolId],
    queryFn: async () => {
      if (!token) throw new Error("No token");
      try {
        const response = await api.templates.getAll(token);
        return response.templates ?? [];
      } catch (error) {
        console.error("Error loading templates:", error);
        throw error;
      }
    },
  });

// Prefix of every certificate template query; invalidate it after any change.
export const CERTIFICATE_TEMPLATES_QUERY_KEY = ["certificate-templates"] as const;

/** The query behind useCertificateTemplates: the Organisation's own certificate templates (the ready-made ones live in code). */
export const certificateTemplatesQuery = (schoolId: string | undefined, token: string | null | undefined) =>
  queryOptions({
    queryKey: [...CERTIFICATE_TEMPLATES_QUERY_KEY, schoolId],
    queryFn: async () => {
      if (!token || !schoolId) throw new Error("No token");
      const response = await api.certificates.listTemplates(token);
      return response.certificateTemplates ?? [];
    },
  });

// Prefix of every Award query; invalidate it after any change.
export const AWARDS_QUERY_KEY = ["awards"] as const;

/** The query behind useAwards: every Award, as Awards lists them. */
export const awardsQuery = (schoolId: string | undefined, token: string | null | undefined) =>
  queryOptions({
    queryKey: [...AWARDS_QUERY_KEY, schoolId],
    queryFn: async () => {
      if (!token || !schoolId) throw new Error("No token");
      const response = await api.certificates.listAwards(token);
      return response.awards ?? [];
    },
  });

/** The query behind useAward: one Award with its Certificates. */
export const awardQuery = (
  schoolId: string | undefined,
  token: string | null | undefined,
  awardId: string | undefined
) =>
  queryOptions({
    queryKey: [...AWARDS_QUERY_KEY, schoolId, awardId],
    queryFn: async () => {
      if (!token || !schoolId || !awardId) throw new Error("No token");
      return api.certificates.getAward(awardId, token);
    },
  });

// Prefix of every Planner query; invalidate it after any change.
export const PLANNERS_QUERY_KEY = ["planners"] as const;

/** The query behind usePlanners: every Planner, Draft or Sent. */
export const plannersQuery = (schoolId: string | undefined, token: string | null | undefined) =>
  queryOptions({
    queryKey: [...PLANNERS_QUERY_KEY, schoolId],
    queryFn: async () => {
      if (!token || !schoolId) throw new Error("No token");
      const response = await api.planners.listPlanners(schoolId, token);
      return response.planners ?? [];
    },
  });

/** The query behind usePlanner: one Planner. */
export const plannerQuery = (
  schoolId: string | undefined,
  token: string | null | undefined,
  plannerId: string | undefined
) =>
  queryOptions({
    queryKey: [...PLANNERS_QUERY_KEY, schoolId, plannerId],
    queryFn: async () => {
      if (!token || !schoolId) throw new Error("No token");
      if (!plannerId) throw new Error("No Planner ID");
      return (await api.planners.getPlanner(schoolId, plannerId, token)).planner;
    },
  });

const getValidNoticeType = (
  type: string | undefined
): "info" | "urgent" | "event" | "image" | "image_notice" | "custom_event" | "direct" | "poll" => {
  if (!type) return "info";
  const validType = (type as string).toLowerCase();
  return validType === "info" ||
    validType === "urgent" ||
    validType === "event" ||
    validType === "image" ||
    validType === "image_notice" ||
    validType === "custom_event" ||
    validType === "direct" ||
    validType === "poll"
    ? validType
    : "info";
};
